import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Plus, Trash, CheckCircle, PencilSimple, WarningCircle, UploadSimple, CaretDown, CaretRight } from "@phosphor-icons/react";
import { toast } from "sonner";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const blank = {
  trip_datetime: "", exit_datetime: "", entry_point: "", exit_point: "", amount: "", reference: "", notes: "",
  booking_id: "", customer_id: "", customer_name: "", vehicle_id: "", vehicle_label: "", vehicle_plate: "",
};

export default function TollsPanel() {
  const [tolls, setTolls] = useState([]);
  const [rentals, setRentals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(blank);
  const [match, setMatch] = useState(null);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [showUnmatched, setShowUnmatched] = useState(true);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(() => new Set());
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [t, r] = await Promise.all([
        api.get("/tolls"),
        api.get("/bookings", { params: { booking_type: "rental" } }),
      ]);
      setTolls(t.data);
      setRentals(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (editingId || form.booking_id) return;
    if (!form.vehicle_plate) { setMatch(null); return; }
    const id = setTimeout(async () => {
      try {
        const res = await api.get("/tickets/match", {
          params: { plate: form.vehicle_plate, violation_datetime: form.trip_datetime || "" },
        });
        setMatch(res.data);
      } catch { setMatch(null); }
    }, 450);
    return () => clearTimeout(id);
  }, [form.vehicle_plate, form.trip_datetime, form.booking_id, editingId]);

  const openNew = () => { setForm(blank); setEditingId(null); setMatch(null); setOpen(true); };
  const openEdit = (t) => { setForm({ ...blank, ...t, amount: t.amount ?? "" }); setEditingId(t.id); setMatch(null); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, amount: parseFloat(form.amount) || 0 };
      if (editingId) await api.put(`/tolls/${editingId}`, payload);
      else await api.post("/tolls", payload);
      toast.success(editingId ? "Toll updated" : "Toll added");
      setOpen(false);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const onImport = async (e) => {
    const file = e.target.files?.[0];
    if (file) { setImporting(true);
      try {
        const fd = new FormData();
        fd.append("file", file);
        const res = await api.post("/tolls/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
        const d = res.data;
        const extra = d.skipped_flagged ? ` · ${d.skipped_flagged} flagged skipped` : "";
        toast.success(`Imported ${d.imported} trip(s) · ${d.skipped_duplicates} duplicate(s) skipped · ${d.unmatched} unmatched${extra}`);
        await load();
      } catch (err) { toast.error(formatApiError(err)); }
      finally { setImporting(false); if (fileRef.current) fileRef.current.value = ""; }
    }
  };

  const payOne = async (t) => { try { await api.post(`/tolls/${t.id}/pay`); toast.success("Marked paid"); await load(); } catch (e) { toast.error(formatApiError(e)); } };
  const payAll = async (customer_id, name) => { try { const res = await api.post("/tolls/pay-all", { customer_id }); toast.success(`Paid ${res.data.paid} toll(s) for ${name}`); await load(); } catch (e) { toast.error(formatApiError(e)); } };
  const remove = async (t) => { if (!window.confirm("Delete this 407 toll?")) return; try { await api.delete(`/tolls/${t.id}`); toast.success("Deleted"); await load(); } catch (e) { toast.error(formatApiError(e)); } };

  const isUnmatched = (t) => !t.customer_id && !(t.customer_name && t.customer_name.trim());
  const q = search.trim().toLowerCase();
  const { unmatchedItems, groupList } = useMemo(() => {
    const matchToll = (t) =>
      !q ||
      [t.customer_name, t.vehicle_label, t.vehicle_plate, t.entry_point, t.exit_point, t.reference, t.notes, String(t.amount ?? "")]
        .some((x) => (x || "").toString().toLowerCase().includes(q));
    const filtered = tolls.filter(matchToll);
    const unmatched = filtered.filter(isUnmatched);
    const g = {};
    for (const t of filtered) {
      if (isUnmatched(t)) continue;
      const key = t.customer_id || `name:${t.customer_name}`;
      if (!g[key]) g[key] = { customer_id: t.customer_id, name: t.customer_name || "Unassigned", items: [], recent: "" };
      g[key].items.push(t);
      const c = t.created_at || "";
      if (c > g[key].recent) g[key].recent = c;
    }
    return { unmatchedItems: unmatched, groupList: Object.values(g).sort((a, b) => (b.recent || "").localeCompare(a.recent || "")) };
  }, [tolls, q]);
  const toggleGroup = (key) => setExpanded((prev) => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  const totalOutstanding = tolls.filter((t) => t.status !== "paid").reduce((s, t) => s + (t.amount || 0), 0);
  const fmtDate = (s) => (s ? new Date(s).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

  return (
    <div className="space-y-6" data-testid="tolls-panel">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <p className="text-sm text-muted-foreground">
          Outstanding 407 tolls across all clients: <span className="text-primary font-heading">{money(totalOutstanding)}</span>
        </p>
        <div className="flex gap-2 items-center flex-wrap">
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search client, plate, vehicle…" className="bg-input border-border h-9 w-56" data-testid="toll-search" />
          <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={onImport} className="hidden" data-testid="toll-import-input" />
          <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={importing} data-testid="import-tolls-btn">
            <UploadSimple size={14} className="mr-1.5" /> {importing ? "Importing…" : "Import 407 CSV"}
          </Button>
          <Button onClick={openNew} data-testid="add-toll-btn"><Plus size={14} className="mr-1.5" /> Add toll</Button>
        </div>
      </div>

      {loading ? (
        <div className="text-muted-foreground">Loading tolls…</div>
      ) : groupList.length === 0 && unmatchedItems.length === 0 ? (
        <div className="border border-border bg-card p-12 text-center text-muted-foreground" data-testid="tolls-empty">
          {q ? "No 407 tolls match your search." : "No 407 tolls yet. Import your monthly 407 statement (CSV) or click “Add toll”."}
        </div>
      ) : (
        <>
        {unmatchedItems.length > 0 && (
          <div className="border border-amber-700/50 bg-amber-950/10" data-testid="toll-unmatched-group">
            <button type="button" onClick={() => setShowUnmatched((v) => !v)} className="w-full px-6 py-4 border-b border-amber-700/40 flex items-center justify-between gap-3 text-left" data-testid="toll-unmatched-toggle">
              <div className="flex items-center gap-2">
                {showUnmatched ? <CaretDown size={16} className="text-amber-500" /> : <CaretRight size={16} className="text-amber-500" />}
                <div>
                  <h3 className="font-heading text-lg flex items-center gap-2"><WarningCircle size={18} className="text-amber-500" /> Unmatched</h3>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {unmatchedItems.length} trip(s) with no matching rental or client · total {money(unmatchedItems.reduce((s, t) => s + (t.amount || 0), 0))} — edit each to assign a client
                  </div>
                </div>
              </div>
            </button>
            {showUnmatched && (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left px-4 py-3 label-mono font-normal">Trip date</th>
                    <th className="text-left px-4 py-3 label-mono font-normal">Vehicle</th>
                    <th className="text-left px-4 py-3 label-mono font-normal">Plate</th>
                    <th className="text-left px-4 py-3 label-mono font-normal">Amount</th>
                    <th className="text-left px-4 py-3 label-mono font-normal">Status</th>
                    <th className="text-right px-4 py-3 label-mono font-normal">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {unmatchedItems.map((t) => (
                    <tr key={t.id} className="border-b border-border/40" data-testid={`toll-row-${t.id}`}>
                      <td className="px-4 py-3 text-xs">{fmtDate(t.trip_datetime)}</td>
                      <td className="px-4 py-3 text-xs">{t.vehicle_label || "—"}</td>
                      <td className="px-4 py-3 text-xs">{t.vehicle_plate || "—"}</td>
                      <td className="px-4 py-3 font-heading">{money(t.amount)}</td>
                      <td className="px-4 py-3">{t.status === "paid" ? <Badge className="bg-emerald-600 hover:bg-emerald-600">Paid</Badge> : <Badge variant="destructive">Unpaid</Badge>}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <Button size="icon" variant="ghost" title="Assign client / edit" onClick={() => openEdit(t)} data-testid={`edit-toll-${t.id}`}><PencilSimple size={15} /></Button>
                          <Button size="icon" variant="ghost" title="Delete" onClick={() => remove(t)} data-testid={`delete-toll-${t.id}`}><Trash size={15} className="text-destructive" /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
        {groupList.map((g) => {
          const groupTotal = g.items.reduce((s, t) => s + (t.amount || 0), 0);
          const unpaid = g.items.filter((t) => t.status !== "paid");
          const unpaidTotal = unpaid.reduce((s, t) => s + (t.amount || 0), 0);
          const gkey = g.customer_id || g.name;
          const isOpen = q ? true : expanded.has(gkey);
          return (
            <div key={gkey} className="border border-border bg-card" data-testid={`toll-group-${gkey}`}>
              <div className="px-6 py-4 border-b border-border flex items-center justify-between flex-wrap gap-3">
                <button type="button" onClick={() => toggleGroup(gkey)} className="flex items-center gap-2 text-left" data-testid={`toll-group-toggle-${gkey}`}>
                  {isOpen ? <CaretDown size={16} className="text-muted-foreground" /> : <CaretRight size={16} className="text-muted-foreground" />}
                  <div>
                    <h3 className="font-heading text-lg">{g.name}</h3>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {g.items.length} trip(s) · total {money(groupTotal)} · <span className="text-destructive">{money(unpaidTotal)} unpaid</span>
                    </div>
                  </div>
                </button>
                {g.customer_id && unpaid.length > 0 && (
                  <Button size="sm" variant="outline" onClick={() => payAll(g.customer_id, g.name)} data-testid={`toll-pay-all-${g.customer_id}`}>
                    <CheckCircle size={14} className="mr-1.5" /> Pay all ({unpaid.length})
                  </Button>
                )}
              </div>
              {isOpen && (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left px-4 py-3 label-mono font-normal">Trip date</th>
                    <th className="text-left px-4 py-3 label-mono font-normal">Route</th>
                    <th className="text-left px-4 py-3 label-mono font-normal">Vehicle</th>
                    <th className="text-left px-4 py-3 label-mono font-normal">Amount</th>
                    <th className="text-left px-4 py-3 label-mono font-normal">Source</th>
                    <th className="text-left px-4 py-3 label-mono font-normal">Status</th>
                    <th className="text-right px-4 py-3 label-mono font-normal">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {g.items.map((t) => (
                    <tr key={t.id} className="border-b border-border/40" data-testid={`toll-row-${t.id}`}>
                      <td className="px-4 py-3 text-xs">{fmtDate(t.trip_datetime)}</td>
                      <td className="px-4 py-3 text-xs">{[t.entry_point, t.exit_point].filter(Boolean).join(" → ") || "—"}</td>
                      <td className="px-4 py-3 text-xs">{t.vehicle_label || t.vehicle_plate || "—"}</td>
                      <td className="px-4 py-3 font-heading">{money(t.amount)}</td>
                      <td className="px-4 py-3"><span className="label-mono text-muted-foreground capitalize">{t.source || "manual"}</span></td>
                      <td className="px-4 py-3">{t.status === "paid" ? <Badge className="bg-emerald-600 hover:bg-emerald-600">Paid</Badge> : <Badge variant="destructive">Unpaid</Badge>}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          {t.status !== "paid" && (
                            <Button size="icon" variant="ghost" title="Mark paid" onClick={() => payOne(t)} data-testid={`pay-toll-${t.id}`}>
                              <CheckCircle size={15} className="text-emerald-500" />
                            </Button>
                          )}
                          <Button size="icon" variant="ghost" title="Edit" onClick={() => openEdit(t)} data-testid={`edit-toll-${t.id}`}><PencilSimple size={15} /></Button>
                          <Button size="icon" variant="ghost" title="Delete" onClick={() => remove(t)} data-testid={`delete-toll-${t.id}`}><Trash size={15} className="text-destructive" /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              )}
            </div>
          );
        })}
        </>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="toll-dialog">
          <DialogHeader><DialogTitle>{editingId ? "Edit 407 toll" : "Add 407 toll"}</DialogTitle>
            <DialogDescription>Auto-matches the rental & client by plate and trip date; override below if needed.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="label-mono">Trip date / time</Label>
              <Input type="datetime-local" step={300} value={form.trip_datetime?.slice(0, 16) || ""} onChange={(e) => set("trip_datetime", e.target.value)} className="bg-input border-border mt-1.5" data-testid="toll-datetime-input" />
            </div>
            <div>
              <Label className="label-mono">License plate</Label>
              <Input value={form.vehicle_plate} onChange={(e) => set("vehicle_plate", e.target.value)} placeholder="e.g. CABC123" className="bg-input border-border mt-1.5" data-testid="toll-plate-input" />
            </div>
            <div>
              <Label className="label-mono">Entry point</Label>
              <Input value={form.entry_point} onChange={(e) => set("entry_point", e.target.value)} className="bg-input border-border mt-1.5" data-testid="toll-entry-input" />
            </div>
            <div>
              <Label className="label-mono">Exit point</Label>
              <Input value={form.exit_point} onChange={(e) => set("exit_point", e.target.value)} className="bg-input border-border mt-1.5" data-testid="toll-exit-input" />
            </div>

            {!editingId && (
              <div className="col-span-2">
                {match?.matched ? (
                  <div className="border border-emerald-700/50 bg-emerald-950/30 p-3 text-sm flex items-start gap-2" data-testid="toll-match-found">
                    <CheckCircle size={16} className="text-emerald-500 mt-0.5" />
                    <div>
                      <div className="text-emerald-400">Matched rental</div>
                      <div className="text-muted-foreground text-xs mt-0.5">{match.booking.customer_name || "Unknown client"} · {match.booking.vehicle_label || match.booking.vehicle_plate}</div>
                    </div>
                  </div>
                ) : form.vehicle_plate ? (
                  <div className="border border-amber-700/40 bg-amber-950/20 p-3 text-sm flex items-start gap-2" data-testid="toll-match-none">
                    <WarningCircle size={16} className="text-amber-500 mt-0.5" />
                    <div className="text-muted-foreground text-xs">No rental auto-matched. Pick the rental manually below if you know it.</div>
                  </div>
                ) : null}
              </div>
            )}

            <div className="col-span-2">
              <Label className="label-mono">Rental (override match)</Label>
              <Select value={form.booking_id || "none"} onValueChange={(v) => set("booking_id", v === "none" ? "" : v)}>
                <SelectTrigger className="bg-input border-border mt-1.5" data-testid="toll-booking-select"><SelectValue placeholder="Auto-match by plate & date" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Auto-match by plate & date</SelectItem>
                  {rentals.map((b) => (
                    <SelectItem key={b.id} value={b.id}>
                      {b.customer_name || "Guest"} · {b.vehicle_label || b.vehicle_plate || "—"} · {b.start_datetime ? new Date(b.start_datetime).toLocaleDateString() : "no date"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="label-mono">Amount</Label>
              <Input type="number" value={form.amount} onChange={(e) => set("amount", e.target.value)} className="bg-input border-border mt-1.5" data-testid="toll-amount-input" />
            </div>
            <div>
              <Label className="label-mono">407 reference #</Label>
              <Input value={form.reference} onChange={(e) => set("reference", e.target.value)} className="bg-input border-border mt-1.5" data-testid="toll-reference-input" />
            </div>
            <div className="col-span-2">
              <Label className="label-mono">Notes</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} className="bg-input border-border mt-1.5" data-testid="toll-notes-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} data-testid="toll-cancel-btn">Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="toll-save-btn">{saving ? "Saving…" : editingId ? "Update" : "Add toll"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
